import { useState } from "react";
import { useReviews, useSubmitReview, type Review } from "@/hooks/use-reviews";
import { relativeTime } from "@/lib/utils";

export function ReviewsSection({
  outcomeId,
  canReview,
  jobId,
}: {
  outcomeId: string;
  canReview: boolean;
  jobId?: string;
}) {
  const { data: reviews, isLoading } = useReviews(outcomeId);

  return (
    <section>
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold">Reviews</h2>
        {reviews && reviews.length > 0 ? (
          <span className="text-xs text-neutral-500">
            {reviews.length} shown
          </span>
        ) : null}
      </div>

      {canReview ? (
        <div className="mt-4">
          <ReviewForm outcomeId={outcomeId} jobId={jobId} />
        </div>
      ) : null}

      {isLoading ? (
        <div className="mt-6 space-y-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <div
              key={i}
              className="h-20 animate-pulse rounded-lg bg-neutral-100 dark:bg-neutral-900"
            />
          ))}
        </div>
      ) : (reviews?.length ?? 0) === 0 ? (
        <p className="mt-6 rounded-lg border border-dashed border-neutral-300 p-6 text-center text-sm text-neutral-500 dark:border-neutral-700">
          No reviews yet. Be the first after your next completed job.
        </p>
      ) : (
        <ul className="mt-6 space-y-3">
          {reviews!.map((r) => (
            <ReviewRow key={r.id} review={r} />
          ))}
        </ul>
      )}
    </section>
  );
}

function ReviewRow({ review }: { review: Review }) {
  return (
    <li className="rounded-lg border border-neutral-200 bg-white p-4 text-sm dark:border-neutral-800 dark:bg-neutral-950">
      <div className="flex items-center justify-between">
        <Stars value={review.rating} />
        <span className="text-xs text-neutral-500">
          {relativeTime(review.created_at)}
        </span>
      </div>
      {review.review_text ? (
        <p className="mt-2 text-neutral-800 dark:text-neutral-200">
          {review.review_text}
        </p>
      ) : null}
      <p className="mt-2 text-xs text-neutral-400">
        reviewer <code className="font-mono">{review.reviewer_id}</code>
      </p>
    </li>
  );
}

function Stars({ value }: { value: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <span
          key={i}
          className={
            i < value ? "text-amber-400" : "text-neutral-300 dark:text-neutral-700"
          }
        >
          ★
        </span>
      ))}
    </div>
  );
}

function ReviewForm({ outcomeId, jobId }: { outcomeId: string; jobId?: string }) {
  const [rating, setRating] = useState(0);
  const [text, setText] = useState("");
  const submit = useSubmitReview(outcomeId);

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (rating === 0) return;
        submit.mutate({ rating, review_text: text.trim() || undefined, job_id: jobId });
      }}
      className="rounded-lg border border-neutral-200 bg-neutral-50 p-4 dark:border-neutral-800 dark:bg-neutral-900"
    >
      <p className="text-sm font-medium">Leave a review</p>
      <div className="mt-2 flex gap-1 text-2xl">
        {Array.from({ length: 5 }).map((_, i) => (
          <button
            key={i}
            type="button"
            onClick={() => setRating(i + 1)}
            className={
              i < rating
                ? "text-amber-400 hover:text-amber-500"
                : "text-neutral-300 hover:text-neutral-400 dark:text-neutral-700"
            }
            aria-label={`${i + 1} stars`}
          >
            ★
          </button>
        ))}
      </div>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        maxLength={500}
        rows={3}
        placeholder="What worked well? What could be better?"
        className="mt-3 w-full rounded-md border border-neutral-300 bg-white px-3 py-2 text-sm focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 focus:outline-none dark:border-neutral-700 dark:bg-neutral-950"
      />
      <div className="mt-3 flex items-center justify-between">
        <span className="text-xs text-neutral-500">{text.length} / 500</span>
        <button
          type="submit"
          disabled={rating === 0 || submit.isPending}
          className="rounded-md bg-emerald-500 px-4 py-1.5 text-xs font-medium text-white hover:bg-emerald-600 disabled:opacity-50"
        >
          {submit.isPending ? "Submitting…" : "Submit review"}
        </button>
      </div>
      {submit.isError ? (
        <p className="mt-2 text-xs text-red-500">
          {(submit.error as Error).message}
        </p>
      ) : null}
    </form>
  );
}
